import * as React from 'react';
import { cn } from './utils';

/**
 * DataList — labelled key/value rows for read-only summaries.
 *
 * Compose as `<DataList>` containing `<DataListRow>` children. Empty values
 * (undefined / empty string) are skipped automatically so callers can pass
 * the raw form state without pre-filtering.
 *
 * Mobile: stacked label-above-value. Desktop (sm+): label left, value right
 * with a fixed label gutter so adjacent rows align.
 */

export interface DataListProps extends React.HTMLAttributes<HTMLDListElement> {}

export function DataList({
  className,
  children,
  ...props
}: DataListProps): JSX.Element {
  return (
    <dl className={cn('grid gap-1.5', className)} {...props}>
      {children}
    </dl>
  );
}

export interface DataListRowProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /**
   * When `true`, the row renders even with an empty value (shows an em-dash).
   * Default behaviour omits the row entirely so callers can pass partial data
   * without conditional wrappers.
   */
  showEmpty?: boolean;
  className?: string;
}

export function DataListRow({
  label,
  value,
  showEmpty = false,
  className,
}: DataListRowProps): JSX.Element | null {
  const isEmpty =
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim().length === 0);
  if (isEmpty && !showEmpty) return null;
  return (
    <div className={cn('flex flex-col gap-0.5 sm:flex-row sm:gap-4', className)}>
      <dt className="w-44 shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm text-foreground">
        {isEmpty ? <span className="text-muted-foreground">—</span> : value}
      </dd>
    </div>
  );
}
